import { useState, useEffect, useMemo } from 'react';

import { bilingualLines, loadSearchNormalizer, normalizeForSearch } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';

interface ListItem {
  itemType: 'activity' | 'pass';
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  isFeatured: boolean;
  featuredOrder: number | null;
  images: Array<{ imageUrl: string }>;
}

interface FeaturedItemRef {
  type: 'activity' | 'pass';
  id: string;
}

interface FeaturedActivitiesManagerProps {
  getToken: () => Promise<string | undefined>;
}

export function FeaturedActivitiesManager({ getToken }: FeaturedActivitiesManagerProps) {
  const [allItems, setAllItems] = useState<ListItem[]>([]);
  const [featuredItems, setFeaturedItems] = useState<FeaturedItemRef[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const siteLanguage = useSiteLanguage();
  // Title search ignores Simplified/Traditional differences (PRD §5.5).
  const [normalizerReady, setNormalizerReady] = useState(false);
  useEffect(() => {
    void loadSearchNormalizer().then(() => setNormalizerReady(true));
  }, []);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const response = await fetch(`${apiUrl}/admin/featured-activities`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error('Failed to fetch data');

        const result = await response.json();
        if (result.success) {
          const { activities, passes } = result.data;

          // Normalize both lists into unified ListItem format
          const activityItems: ListItem[] = (activities || []).map(
            (a: ListItem & { itemType?: string }) => ({
              ...a,
              itemType: 'activity' as const,
            })
          );
          const passItems: ListItem[] = (passes || []).map(
            (p: ListItem & { itemType?: string }) => ({
              ...p,
              itemType: 'pass' as const,
            })
          );

          const combined = [...activityItems, ...passItems];
          setAllItems(combined);

          // Initialize featured items from those already featured, sorted by featuredOrder
          const featured = combined
            .filter((item) => item.isFeatured)
            .sort((a, b) => (a.featuredOrder ?? 0) - (b.featuredOrder ?? 0))
            .map((item) => ({ type: item.itemType, id: item.id }));
          setFeaturedItems(featured);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, getToken]);

  // Check if an item is in the featured list
  const isFeaturedItem = (itemType: 'activity' | 'pass', id: string) =>
    featuredItems.some((f) => f.type === itemType && f.id === id);

  // Available (non-featured) items filtered by search
  const availableItems = useMemo(() => {
    return allItems
      .filter((item) => !isFeaturedItem(item.itemType, item.id))
      .filter((item) => {
        if (!searchQuery) return true;
        const query = normalizeForSearch(searchQuery);
        return [item.titleCN, item.titleEN].some(
          (title) => !!title && normalizeForSearch(title).includes(query)
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, featuredItems, searchQuery, normalizerReady]);

  // Featured items in order, resolved to full ListItem objects
  const featuredListItems = useMemo(() => {
    return featuredItems
      .map((ref) => allItems.find((item) => item.itemType === ref.type && item.id === ref.id))
      .filter((item): item is ListItem => item !== undefined);
  }, [allItems, featuredItems]);

  const handleAddFeatured = (itemType: 'activity' | 'pass', id: string) => {
    if (featuredItems.length >= 8) {
      setError('Maximum 8 featured items allowed');
      return;
    }
    setFeaturedItems([...featuredItems, { type: itemType, id }]);
    setHasChanges(true);
    setError(null);
  };

  const handleRemoveFeatured = (index: number) => {
    setFeaturedItems(featuredItems.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newItems = [...featuredItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setFeaturedItems(newItems);
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index >= featuredItems.length - 1) return;
    const newItems = [...featuredItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setFeaturedItems(newItems);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = await getToken();

      const response = await fetch(`${apiUrl}/admin/featured-activities`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items: featuredItems }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to save');
      }

      setHasChanges(false);
      // Update local state to reflect changes
      setAllItems(
        allItems.map((item) => {
          const featuredIndex = featuredItems.findIndex(
            (f) => f.type === item.itemType && f.id === item.id
          );
          return {
            ...item,
            isFeatured: featuredIndex !== -1,
            featuredOrder: featuredIndex !== -1 ? featuredIndex : null,
          };
        })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const TypeBadge = ({ type }: { type: 'activity' | 'pass' }) => (
    <span
      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
        type === 'pass' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {type === 'pass' ? 'Pass' : 'Activity'}
    </span>
  );

  return (
    <div>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Items */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Available Items</h3>
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search activities & passes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
            {availableItems.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchQuery ? 'No matching items' : 'No available items'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {availableItems.map((item) => (
                  <li
                    key={`${item.itemType}-${item.id}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {item.images[0] && (
                        <img
                          src={item.images[0].imageUrl}
                          alt=""
                          className="w-10 h-10 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <ItemTitle zh={item.titleCN} en={item.titleEN} lang={siteLanguage} />
                        <TypeBadge type={item.itemType} />
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddFeatured(item.itemType, item.id)}
                      disabled={featuredItems.length >= 8}
                      className="ml-2 px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Featured Items */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Featured (homepage order)</h3>
          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
            {featuredListItems.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No featured items selected
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {featuredListItems.map((item, index) => (
                  <li
                    key={`${item.itemType}-${item.id}`}
                    className="flex items-center justify-between p-3 bg-white"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-gray-400 w-5">{index + 1}.</span>
                      {item.images[0] && (
                        <img
                          src={item.images[0].imageUrl}
                          alt=""
                          className="w-10 h-10 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <ItemTitle zh={item.titleCN} en={item.titleEN} lang={siteLanguage} />
                        <TypeBadge type={item.itemType} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        disabled={index === featuredListItems.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemoveFeatured(index)}
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Remove"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-4 flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

/** Two-line title, the user's language on top (PRD §5.2). */
function ItemTitle({ zh, en, lang }: { zh: string | null; en: string | null; lang: string }) {
  const [top, bottom] = bilingualLines(zh, en, lang);
  return (
    <>
      <span className="text-sm text-gray-900 truncate block">{top}</span>
      {bottom && <span className="text-xs text-gray-500 truncate block">{bottom}</span>}
    </>
  );
}
